import * as React from 'react';
import {useState, useEffect} from 'react';
import AddPartnerModal from './AddPartnerModal';
import {IAccountDetails} from '../../services/account';
import {IPartner, get as getPartners, reject, approve} from '../../services/partner';
import {
    get as getDatabases,
    IDatabaseBase,
    resetCache as resetDatabaseCache,
} from '../../services/database';

import {PropagateLoader} from 'react-spinners';
import {FormattedMessage, injectIntl, InjectedIntlProps, defineMessages} from 'react-intl';
import Modal from '../../shared/Modal';
import {Link} from 'react-router-dom';
import {DASHBOARD_PATH, PARTNER_PAGE_SIZE} from '../../constants';
import {isEmpty} from 'src/util';
import {getOwner} from '../../services/pages';
import RequestPartnerModal from './RequestPartnerModal';
import _ from 'lodash';

function comparePartners(a: IPartner, b: IPartner) {
    if (a.account.title.toUpperCase() < b.account.title.toUpperCase()) {
        return -1;
    }
    if (a.account.title.toUpperCase() > b.account.title.toUpperCase()) {
        return 1;
    }
    return 0;
}

async function loadPartners(
    type: 'incoming' | 'outgoing',
    page: number,
    currentList: IPartner[],
    partnerSetter: (p: IPartner[]) => void,
    pageCountSetter: (count: number) => void
) {
    const p = await getPartners(type, page);
    if (p.success) {
        await p.partners.reduce(async (promise, partner) => {
            await promise;
            const response = await getOwner(partner.account.name);
            partner.owner = response.owner;
        }, Promise.resolve());

        if (p.partnersPaged)
            pageCountSetter(Math.ceil(p.partnersPaged.hitCount / PARTNER_PAGE_SIZE));
        partnerSetter([...currentList, ...p.partners]);
    }
}

export default injectIntl(
    (
        props: InjectedIntlProps & {
            accountDetails: IAccountDetails;
            updateAlertCounts: IUpdateAlertCounts;
        }
    ) => {
        const [currentPartner] = useState<IPartner | null>(null);
        const [databases, setDatabases] = useState<IDatabaseBase[]>([]);

        const [
            pendingIncomingPartnershipsToMyDatabase,
            setPendingIncomingPartnershipsToMyDatabase,
        ] = useState<IPartner[]>([]);
        const [
            pendingOutgoingPartnershipsToMyDatabase,
            setPendingOutgoingPartnershipsToMyDatabase,
        ] = useState<IPartner[]>([]);
        const [incomingPartnerships, setIncomingPartnerships] = useState<IPartner[]>([]);
        const [incomingPartnerPageCount, setIncomingPartnerPageCount] = useState<number>(1);
        const [incomingPartnerPage, setIncomingPartnerPage] = useState<number>(1);

        const [currentPartnerFilter, setCurrentPartnerFilter] = useState<string>();
        const [currentPartnerDatabaseFilter, setCurrentPartnerDatabaseFilter] = useState<string>();

        const [
            pendingIncomingPartnershipsToOutsideDatabase,
            setPendingIncomingPartnershipsToOutsideDatabase,
        ] = useState<IPartner[]>([]);
        const [
            pendingOutgoingPartnershipsToOutsideDatabase,
            setPendingOutgoingPartnershipsToOutsideDatabase,
        ] = useState<IPartner[]>([]);
        const [outgoingPartnerships, setOutgoingPartnerships] = useState<IPartner[]>([]);
        const [outgoingPartnerPageCount, setOutgoingPartnerPageCount] = useState<number>(1);
        const [outgoingPartnerPage, setOutgoingPartnerPage] = useState<number>(1);

        const [isLoadingPartners, setIsLoadingPartners] = useState<boolean>(false);

        const [tab, setTab] = useState<'client' | 'partner'>('client');

        const [tableSortObj, setTableSortObj] = useState({
            incoming: {column: '', direction: ''},
            pendingIncoming: {column: '', direction: ''},
            outgoing: {column: '', direction: ''},
            pendingOutgoing: {column: '', direction: ''},
        });

        const getArrow = (table, column) => {
            return tableSortObj[table].column === column
                ? tableSortObj[table].direction === 'ASC'
                    ? '↓'
                    : '↑'
                : '';
        };
        const setSortObj = (table, column) => {
            const updatedObj = structuredClone(tableSortObj);
            updatedObj[table].column = column;
            updatedObj[table].direction = tableSortObj[table].direction === 'ASC' ? 'DESC' : 'ASC';
            setTableSortObj(updatedObj);
        };
        const sortData = (data, column, direction) =>
            data.sort((a: IPartner, b: IPartner) => {
                return direction === 'ASC'
                    ? _.get(a, column).localeCompare(_.get(b, column))
                    : _.get(b, column).localeCompare(_.get(a, column));
            });

        const updatePartners = async () => {
            setIsLoadingPartners(true);
            await loadPartners(
                'incoming',
                1,
                [],
                setIncomingPartnerships,
                setIncomingPartnerPageCount
            );
            await loadPartners(
                'outgoing',
                1,
                [],
                setOutgoingPartnerships,
                setOutgoingPartnerPageCount
            );

            const fetchedDatabases = await getDatabases();
            if (fetchedDatabases.success) {
                setDatabases(fetchedDatabases.databases || []);
                if (!(fetchedDatabases.databases || []).length) setTab('partner');
            }

            const fetchedPendingPartners = await getPartners('pending');

            if (fetchedPendingPartners.success) {
                await fetchedPendingPartners.partners.reduce(async (promise, partner) => {
                    await promise;
                    const response = await getOwner(partner.account.name);
                    partner.owner = response.owner;
                }, Promise.resolve());

                const myDatabaseNames = (fetchedDatabases.databases || []).map((d) => d.name);
                const pending = fetchedPendingPartners.partners.filter(
                    (p) => p.direction !== 'outgoing'
                );
                setPendingOutgoingPartnershipsToMyDatabase(
                    fetchedPendingPartners.partners.filter(
                        (p) =>
                            p.direction === 'outgoing' && myDatabaseNames.includes(p.database.name)
                    )
                );
                setPendingOutgoingPartnershipsToOutsideDatabase(
                    fetchedPendingPartners.partners.filter(
                        (p) =>
                            p.direction === 'outgoing' && !myDatabaseNames.includes(p.database.name)
                    )
                );
                setPendingIncomingPartnershipsToMyDatabase(
                    fetchedPendingPartners.partners.filter(
                        (p) =>
                            p.direction !== 'outgoing' && myDatabaseNames.includes(p.database.name)
                    )
                );
                setPendingIncomingPartnershipsToOutsideDatabase(
                    fetchedPendingPartners.partners.filter(
                        (p) =>
                            p.direction !== 'outgoing' && !myDatabaseNames.includes(p.database.name)
                    )
                );
                props.updateAlertCounts({partners: pending.length});
            }
            setIsLoadingPartners(false);
        };

        useEffect(() => {
            loadPartners(
                'incoming',
                incomingPartnerPage,
                [],
                setIncomingPartnerships,
                setIncomingPartnerPageCount
            );
        }, [incomingPartnerPage]);
        useEffect(() => {
            loadPartners(
                'outgoing',
                outgoingPartnerPage,
                [],
                setOutgoingPartnerships,
                setOutgoingPartnerPageCount
            );
        }, [outgoingPartnerPage]);

        const messages = defineMessages({
            tableHdrOrgName: {
                id: 'dash.partners.table.orgName',
                defaultMessage: 'Organization',
            },
            tableHdrAccountOwnerName: {
                id: 'dash.partners.table.AccountOwnerName',
                defaultMessage: 'Owner Name',
            },
            tableHdrAccountOwnerEmail: {
                id: 'dash.partners.table.AccountOwnerEmail',
                defaultMessage: 'Owner Email',
            },
            tableHdrAccountOwnerPhoNum: {
                id: 'dash.partners.table.AccountOwnerPhoNum',
                defaultMessage: 'Owner Phone',
            },
            tableHdrName: {
                id: 'dash.partners.table.name',
                defaultMessage: 'Name',
            },
            tableHdrCity: {
                id: 'dash.partners.table.city',
                defaultMessage: 'City',
            },
            tableHdrDb: {
                id: 'dash.partners.table.db',
                defaultMessage: 'Database',
            },
            tableHdrDelete: {
                id: 'dash.partners.table.delete',
                defaultMessage: 'Delete',
            },
            tableHdrApprove: {
                id: 'dash.partners.table.approve',
                defaultMessage: 'Approve',
            },
            tableHdrReject: {
                id: 'dash.partners.table.reject',
                defaultMessage: 'Reject',
            },
            filterBy: {
                id: 'dash.partners.filter.by',
                defaultMessage: 'Filter result by',
            },
            filters: {
                id: 'dash.partners.filters',
                defaultMessage: 'Filters',
            },
            filterClear: {
                id: 'dash.partners.filter.clear',
                defaultMessage: 'Clear filters',
            },
            dashboard: {
                id: 'dash.partners.bc.dashboard',
                defaultMessage: 'Dashboard',
            },
            partners: {
                id: 'dash.partners.bc.partners',
                defaultMessage: 'Partners',
            },
            outgoingPartnerRequestHeader: {
                id: 'dash.partners.pending.outgoing.hdr',
                defaultMessage: 'Pending Outgoing Partner Requests',
            },
        });

        useEffect(() => {
            updatePartners();
        }, []);

        const removePartner = async (name: string) => {
            const result = await reject(name);
            if (!result.success) alert(result.message);
            updatePartners();
        };

        const approvePartner = async (name: string) => {
            const result = await approve(name);
            resetDatabaseCache();
            if (!result.success) alert(result.message);
            updatePartners();
        };

        const renderPartner = (partner: IPartner) => {
            return (
                <tr className="trcontent" key={partner.name}>
                    <td>{partner.owner?.organization}</td>
                    <td>{partner.owner?.fullName}</td>
                    <td>{partner.owner?.email}</td>
                    <td>
                        {partner.owner?.phone.work
                            ? partner.owner?.phone.work
                            : partner.owner?.phone.mobile}
                    </td>
                    <td>{partner.database.title}</td>
                    {renderApproveButton(partner)}
                    <td className="text-center">{renderRejectButton(partner)}</td>
                </tr>
            );
        };

        const renderRejectButton = (partner: IPartner) => {
            if (partner.status === 'pending' && partner.direction !== 'outgoing') {
                // we 'reject' incoming partner requests/invitations
                return (
                    <button className="icon-btn reject" onClick={() => removePartner(partner.name)}>
                        <i className="fas fa-times" />
                    </button>
                );
            } else {
                // we can delete outgoing requests/invitations or existing partners
                return (
                    <button className="icon-btn" onClick={() => removePartner(partner.name)}>
                        <i className="fas fa-trash-alt" />
                    </button>
                );
            }
        };

        const renderApproveButton = (partner: IPartner) => {
            if (partner.status === 'pending' && partner.direction !== 'outgoing') {
                return (
                    <td className="text-center">
                        <button
                            className="icon-btn approve"
                            onClick={() => approvePartner(partner.name)}>
                            <i className="fas fa-check" />
                        </button>
                    </td>
                );
            } else {
                return null;
            }
        };

        const renderAddButton = () => {
            if (tab === 'client')
                return (
                    <AddPartnerModal
                        onSave={updatePartners}
                        accountDetails={props.accountDetails}
                        buttonClassName="mt-3"
                        buttonStyle={{position: 'absolute', right: 25, zIndex: 1}}
                    />
                );
            return (
                <RequestPartnerModal
                    onSave={updatePartners}
                    accountDetails={props.accountDetails}
                    buttonClassName="mt-3"
                    buttonStyle={{position: 'absolute', right: 25, zIndex: 1}}
                />
            );
        };

        const renderNoPartners = () => {
            if (tab === 'client')
                return (
                    <>
                        <h3 className="text-muted my-5 col-12 text-center">
                            {isLoadingPartners ? (
                                <div className="p-5 d-flex justify-content-center">
                                    <PropagateLoader
                                        size={15}
                                        loading={isLoadingPartners}
                                        color="#29447b"
                                    />
                                </div>
                            ) : props.accountDetails.subscriptionLevel > 0 ? (
                                databases.length ? (
                                    <FormattedMessage
                                        id="dash.partners.mine.empty.hdr"
                                        defaultMessage="You are not sharing any of your databases with any partners."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="dash.partners.mine.databases.empty.hdr"
                                        defaultMessage="You current have no databases to share with partners. Use your dashboard to create a database."
                                    />
                                )
                            ) : (
                                <FormattedMessage
                                    id="dash.partners.upgrade.hdr"
                                    defaultMessage="A subscription is required to create a database and add partners. Contact Harmonize® sales to add a subscription to your account."
                                />
                            )}
                        </h3>
                        <div className="d-flex justify-content-center col-12">
                            {props.accountDetails.subscriptionLevel > 0 && databases.length > 0 && (
                                <AddPartnerModal
                                    onSave={updatePartners}
                                    accountDetails={props.accountDetails}
                                    buttonClassName="mt-0"
                                />
                            )}
                        </div>
                    </>
                );
            return isLoadingPartners ? (
                <div className="text-muted my-5 col-12 text-center">
                    <div className="p-5 d-flex justify-content-center">
                        <PropagateLoader size={15} loading={isLoadingPartners} color="#29447b" />
                    </div>
                </div>
            ) : (
                <>
                    <h3 className="text-muted my-5 col-12 text-center">
                        <FormattedMessage
                            id="dash.partners.empty.hdr"
                            defaultMessage="You are not a partner on any databases."
                        />
                    </h3>
                    <div className="d-flex justify-content-center col-12">
                        <RequestPartnerModal
                            onSave={updatePartners}
                            accountDetails={props.accountDetails}
                            buttonClassName="mt-0"
                        />
                    </div>
                </>
            );
        };

        const renderPartners = (table, partners: IPartner[], page) => {
            if (isEmpty(partners)) return null;
            let filteredPartners = partners.filter(
                (p) =>
                    (!currentPartnerFilter || p.account.name === currentPartnerFilter) &&
                    (!currentPartnerDatabaseFilter ||
                        p.database.name === currentPartnerDatabaseFilter)
            );

            if (tableSortObj[table].column) {
                filteredPartners = sortData(
                    filteredPartners,
                    tableSortObj[table].column,
                    tableSortObj[table].direction
                );
            }

            filteredPartners = filteredPartners.slice(
                (page - 1) * PARTNER_PAGE_SIZE,
                (page - 1) * PARTNER_PAGE_SIZE + PARTNER_PAGE_SIZE
            );

            return (
                <div className="col-12 py-3">
                    <FormattedMessage
                        id="dash.partners.current.hdr"
                        defaultMessage="Current Partners">
                        {(txt) => <div className="h4 sub-title">{txt}</div>}
                    </FormattedMessage>
                    <p className="text-muted">
                        {tab === 'client' ? (
                            <FormattedMessage
                                id="dash.info.partners.mine.txt"
                                defaultMessage={`Below is a list of partners currently linked with one or more of your databases. Click "Delete" to end a partnership and remove that partner from a database. Use the dropdown box to filter your list.`}
                            />
                        ) : (
                            <FormattedMessage
                                id="dash.info.partners.others.txt"
                                defaultMessage={`Below is a list of databases owned by other organizations that you are currently partnered with. Click "Delete" to end a partnership and remove your organization from a database. Use the dropdown box to filter your list.`}
                            />
                        )}
                    </p>
                    {partners.length > 10 ? (
                        <>
                            <select onChange={(e) => setCurrentPartnerFilter(e.target.value)}>
                                <option value="">Show all partners</option>
                                {partners
                                    .map((x) => x.account)
                                    .filter(
                                        (value, index, self) =>
                                            self.findIndex((s) => s.name === value.name) === index
                                    )
                                    .map((o) => (
                                        <option key={`${o.name}-filter-option`} value={o.name}>
                                            {o.title}
                                        </option>
                                    ))}
                            </select>
                            <select
                                className="ml-3"
                                onChange={(e) => setCurrentPartnerDatabaseFilter(e.target.value)}>
                                <option value="">Show all databases</option>
                                {partners
                                    .map((x) => x.database)
                                    .filter(
                                        (value, index, self) =>
                                            self.findIndex((s) => s.name === value.name) === index
                                    )
                                    .map((o) => (
                                        <option key={`${o.name}-filter-option`} value={o.name}>
                                            {o.title}
                                        </option>
                                    ))}
                            </select>
                        </>
                    ) : null}
                    <div className="admintable">
                        <table>
                            <tbody>
                                <tr className="trhead">
                                    <th onClick={(e) => setSortObj(table, 'account.title')}>
                                        {props.intl.formatMessage(messages.tableHdrOrgName)}
                                        {getArrow(table, 'account.title')}
                                    </th>
                                    <th>
                                        {props.intl.formatMessage(
                                            messages.tableHdrAccountOwnerName
                                        )}
                                    </th>
                                    <th>
                                        {props.intl.formatMessage(
                                            messages.tableHdrAccountOwnerEmail
                                        )}
                                    </th>
                                    <th>
                                        {props.intl.formatMessage(
                                            messages.tableHdrAccountOwnerPhoNum
                                        )}
                                    </th>
                                    <th onClick={(e) => setSortObj(table, 'database.title')}>
                                        {props.intl.formatMessage(messages.tableHdrDb)}
                                        {getArrow(table, 'database.title')}
                                    </th>
                                    <th className="t-trash mx-auto">
                                        {props.intl.formatMessage(messages.tableHdrDelete)}
                                    </th>
                                </tr>

                                {filteredPartners.map(renderPartner)}
                            </tbody>
                        </table>
                        <div className="searchfltercontent">
                            {tab === 'client'
                                ? renderPager(
                                      incomingPartnerPage,
                                      incomingPartnerPageCount,
                                      setIncomingPartnerPage
                                  )
                                : renderPager(
                                      outgoingPartnerPage,
                                      outgoingPartnerPageCount,
                                      setOutgoingPartnerPage
                                  )}
                        </div>
                        <div className="p-5 d-flex justify-content-center">
                            <PropagateLoader
                                size={15}
                                loading={isLoadingPartners}
                                color="#29447b"
                            />
                        </div>
                    </div>
                </div>
            );
        };

        const renderIncomingPendingPartners = (partners: IPartner[]) => {
            if (isEmpty(partners)) return null;
            if (!isEmpty(partners)) {
                const table = 'pendingIncoming';
                let sortedPartners = partners.slice();

                if (tableSortObj[table].column) {
                    sortedPartners = sortData(
                        sortedPartners,
                        tableSortObj[table].column,
                        tableSortObj[table].direction
                    );
                }

                sortedPartners = sortedPartners.slice(
                    (incomingPartnerPage - 1) * PARTNER_PAGE_SIZE,
                    (incomingPartnerPage - 1) * PARTNER_PAGE_SIZE + PARTNER_PAGE_SIZE
                );

                return (
                    <>
                        <div className="col-12 py-3">
                            <FormattedMessage
                                id="dash.partners.pending.incoming.hdr"
                                defaultMessage="Incoming Partner Requests {count}"
                                values={{count: <span>{partners.length}</span>}}>
                                {(txt) => <h4 className="sub-title">{txt}</h4>}
                            </FormattedMessage>
                            <p className="text-muted font-weight-light">
                                {tab === 'client' ? (
                                    <FormattedMessage
                                        id="dash.partners.pending.incoming.mine.txt"
                                        defaultMessage="Below are requests initiated by other organizations to partner with one of your databases. Please click Accept or Reject to take action."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="dash.partners.pending.incoming.others.txt"
                                        defaultMessage="Below are requests initiated by other organizations for you to partner with one of their databases. Please click Accept or Reject to take action."
                                    />
                                )}
                            </p>
                            <p className="alert alert-primary m-0" role="alert">
                                <i className="fas fa-info-circle pr-3 m-0" />
                                <FormattedMessage
                                    id="dash.partners.pending.instrs"
                                    defaultMessage="Approving the partner will automatically link them to your organization."
                                />
                            </p>
                        </div>

                        <div className="col-12">
                            <div className="admintable">
                                <table>
                                    <tbody>
                                        <tr className="trhead">
                                            <th onClick={(e) => setSortObj(table, 'account.title')}>
                                                {props.intl.formatMessage(messages.tableHdrOrgName)}
                                                {getArrow(table, 'account.title')}
                                            </th>
                                            <th>
                                                {props.intl.formatMessage(
                                                    messages.tableHdrAccountOwnerName
                                                )}
                                            </th>
                                            <th>
                                                {props.intl.formatMessage(
                                                    messages.tableHdrAccountOwnerEmail
                                                )}
                                            </th>
                                            <th>
                                                {props.intl.formatMessage(
                                                    messages.tableHdrAccountOwnerPhoNum
                                                )}
                                            </th>

                                            <th
                                                onClick={(e) =>
                                                    setSortObj(table, 'database.title')
                                                }>
                                                {props.intl.formatMessage(messages.tableHdrDb)}
                                                {getArrow(table, 'database.title')}
                                            </th>
                                            <th className="t-trash mx-auto">
                                                {props.intl.formatMessage(messages.tableHdrApprove)}
                                            </th>
                                            <th className="t-trash mx-auto">
                                                {props.intl.formatMessage(messages.tableHdrDelete)}
                                            </th>
                                        </tr>
                                        {sortedPartners.map(renderPartner)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="clearfix" />
                        <div className="col-lg-12 martop-30">
                            <hr />
                        </div>
                    </>
                );
            }
            return <noscript />;
        };

        const renderOutgoingPendingPartners = (partners: IPartner[]) => {
            const table = 'pendingOutgoing';
            let sortedPartners = partners.slice();

            if (tableSortObj[table].column) {
                sortedPartners = sortData(
                    sortedPartners,
                    tableSortObj[table].column,
                    tableSortObj[table].direction
                );
            }

            sortedPartners = sortedPartners.slice(
                (outgoingPartnerPage - 1) * PARTNER_PAGE_SIZE,
                (outgoingPartnerPage - 1) * PARTNER_PAGE_SIZE + PARTNER_PAGE_SIZE
            );

            if (!isEmpty(partners)) {
                return (
                    <>
                        <div className="col-12 py-3">
                            <h4 className="sub-title" style={{display: 'inline'}}>
                                {props.intl.formatMessage(messages.outgoingPartnerRequestHeader)}
                            </h4>
                        </div>
                        <div className="col-12">
                            <p className="text-muted font-weight-light">
                                {tab === 'client' ? (
                                    <FormattedMessage
                                        id="dash.partners.pending.outgoing.mine.txt"
                                        defaultMessage="Below is a list of partnership requests that your organization has initiated. When accepted, the partner will move to your list of Current Partnerships below."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="dash.partners.pending.outgoing.others.txt"
                                        defaultMessage="Below is the status of your requests to partner with another organization's database."
                                    />
                                )}
                            </p>
                        </div>
                        <div className="col-12">
                            <div className="admintable">
                                <table>
                                    <tbody>
                                        <tr className="trhead">
                                            <th onClick={(e) => setSortObj(table, 'account.title')}>
                                                {props.intl.formatMessage(messages.tableHdrOrgName)}
                                                {getArrow(table, 'account.title')}
                                            </th>
                                            <th>
                                                {props.intl.formatMessage(
                                                    messages.tableHdrAccountOwnerName
                                                )}
                                            </th>
                                            <th>
                                                {props.intl.formatMessage(
                                                    messages.tableHdrAccountOwnerEmail
                                                )}
                                            </th>
                                            <th>
                                                {props.intl.formatMessage(
                                                    messages.tableHdrAccountOwnerPhoNum
                                                )}
                                            </th>

                                            <th
                                                onClick={(e) =>
                                                    setSortObj(table, 'database.title')
                                                }>
                                                {props.intl.formatMessage(messages.tableHdrDb)}
                                                {getArrow(table, 'database.title')}
                                            </th>
                                            <th className="t-trash mx-auto">
                                                {props.intl.formatMessage(messages.tableHdrDelete)}
                                            </th>
                                        </tr>

                                        {sortedPartners.map(renderPartner)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="clearfix" />
                        <div className="col-12 martop-30">
                            <hr />
                        </div>
                    </>
                );
            }
            return <noscript />;
        };

        const renderPager = (
            currentPage: number,
            pageCount: number,
            goToPage: (page: number) => void
        ) => {
            if (pageCount > 1) {
                return (
                    <div className="paginationArea">
                        <ul>
                            {Array.from({length: pageCount}, (x, i) => i).map((i) => (
                                <li key={`pager-${i}`}>
                                    <span
                                        className={i + 1 === currentPage ? 'active' : ''}
                                        onClick={() => goToPage(i + 1)}>
                                        {i + 1}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            }
            return null;
        };

        const renderPage = () => {
            const count =
                tab === 'client'
                    ? pendingIncomingPartnershipsToMyDatabase.length +
                      pendingOutgoingPartnershipsToMyDatabase.length +
                      incomingPartnerships.length
                    : pendingIncomingPartnershipsToOutsideDatabase.length +
                      pendingOutgoingPartnershipsToOutsideDatabase.length +
                      outgoingPartnerships.length;

            if (!count) return renderNoPartners();

            return (
                <>
                    {renderAddButton()}
                    {tab === 'client'
                        ? renderIncomingPendingPartners(pendingIncomingPartnershipsToMyDatabase)
                        : renderIncomingPendingPartners(
                              pendingIncomingPartnershipsToOutsideDatabase
                          )}
                    {tab === 'client'
                        ? renderOutgoingPendingPartners(pendingOutgoingPartnershipsToMyDatabase)
                        : renderOutgoingPendingPartners(
                              pendingOutgoingPartnershipsToOutsideDatabase
                          )}
                    {tab === 'client'
                        ? renderPartners('incoming', incomingPartnerships, incomingPartnerPage)
                        : renderPartners('outgoing', outgoingPartnerships, outgoingPartnerPage)}
                </>
            );
        };

        const renderPartnerDetailModal = () => {
            if (!currentPartner) return null;
            return (
                <Modal
                    large
                    id="partnerDetailModal"
                    title={currentPartner.account.title}
                    actionButtons={
                        currentPartner.status === 'pending'
                            ? [
                                  <button
                                      type="button"
                                      className="btn btn-primary "
                                      key="done-button"
                                      onClick={() => approvePartner(currentPartner.name)}>
                                      Approve
                                  </button>,
                              ]
                            : []
                    }>
                    <div className="form-group md-form d-flex">
                        <div className="card">
                            <h6>Database:</h6>{' '}
                            <span className="text-muted">{currentPartner.database.title}</span>
                        </div>
                        <div className="card">
                            <h6>City:</h6>{' '}
                            <span className="text-muted">{currentPartner.account.city}</span>
                        </div>
                        <div className="card">
                            <h6>Date:</h6>{' '}
                            <span className="text-muted">{currentPartner.created}</span>
                        </div>
                    </div>
                </Modal>
            );
        };
        const renderPageTabNav = () => {
            return (
                <div className="infotab col-lg-12">
                    <ul className="nav navtabs" id="myTab" role="tablist">
                        <li className="nav-item">
                            <a
                                onClick={async () => {
                                    setTab('client');
                                    setCurrentPartnerFilter('');
                                }}
                                className={tab === 'client' ? 'nav-link active' : 'nav-link'}
                                id="client-tab"
                                data-toggle="tab"
                                href="#client"
                                role="tab"
                                aria-controls="client"
                                aria-selected="false">
                                <FormattedMessage
                                    id="dash.partners.tab.client"
                                    defaultMessage="Database Partners"
                                />
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                onClick={async () => {
                                    setTab('partner');
                                    setCurrentPartnerFilter('');
                                }}
                                className={tab === 'partner' ? 'nav-link active' : 'nav-link'}
                                id="partner-tab"
                                data-toggle="tab"
                                href="#partner"
                                role="tab"
                                aria-controls="partner"
                                aria-selected="true">
                                <FormattedMessage
                                    id="dash.partners.tab.partner"
                                    defaultMessage="Our Other Partnerships"
                                />
                            </a>
                        </li>
                    </ul>
                </div>
            );
        };
        return (
            <div className="tab-pane show active mar-top-35 mobo-padding">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 breadcrumb-col">
                            <ul>
                                <li>
                                    <Link to={DASHBOARD_PATH}>
                                        {props.intl.formatMessage(messages.dashboard)}
                                    </Link>
                                </li>
                                <li>
                                    <i className="fas fa-angle-right" />
                                </li>
                                <li>{props.intl.formatMessage(messages.partners)}</li>
                            </ul>
                        </div>
                    </div>
                    <div className="row pagetitle">
                        <div className="col-12">
                            <h2>{props.intl.formatMessage(messages.partners)}</h2>
                        </div>
                    </div>
                    <div className="row">{renderPageTabNav()}</div>
                    <div className="row">{renderPage()}</div>
                </div>
                {renderPartnerDetailModal()}
            </div>
        );
    }
);



// WEBPACK FOOTER //
// ./src/client/dashboard/Partners.tsx